import React, { useEffect, useState } from 'react';
import { Button, message, Spin, Typography, theme } from 'antd';
import { IconDownload, IconFileInvoice, IconMapOff, IconMapPin, IconReceipt2, IconX } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import StaticMap from './StaticMap';
import BookingReferenceTag from './BookingReferenceTag';
import { getHeavyDutyMachineryById } from '../services/HeavyDutyMachineryService';
import { getInvoicesForBooking, downloadInvoice } from '../services/InvoiceService';
import { IHeavyDutyMachinery } from '../interfaces/heavyDutyMachinery/IHeavyDutyMachinery';
import { IBooking } from '../interfaces/heavyDutyMachinery/IBooking';
import { IInvoice } from '../interfaces/heavyDutyMachinery/IInvoice';
import { TRANSLATIONS } from '../constants/translations';
import { translate } from '../helpers/translationHelper';
import { isDailyRentalMode, lastRentalDay } from '../helpers/rentalPeriodHelper';
import { buildBookingLocationView, handoverModeKey } from '../helpers/bookingLocationHelper';
import { CORAL } from '../theme/creamyTokens';
import { useAuth } from '../contextProviders/AuthProvider';
import ConfirmBottomSheet from './ConfirmBottomSheet';
import AddInvoiceTaxInfoModal from './AddInvoiceTaxInfoModal';
import QuoteBreakdownSheet from './QuoteBreakdownSheet';

const { Text, Title } = Typography;

interface BookingDetailsPanelProps {
  booking: IBooking;
  onClose: () => void;
  canCancel: boolean;
  cancelling: boolean;
  onCancel: () => void;
}

/**
 * Right-hand panel of the /messages page -- the Booking (+ its
 * HeavyDutyMachinery, fetched separately since the map coordinates live
 * there, not on the Booking itself): where the machine is, when it changes
 * hands, who is driving it, and what it costs.
 *
 * Three things in here were inherited from the nightly-stay app this was
 * forked from, and all three have gone:
 *
 * - it printed `booking.rentalMode` RAW, so a by-days hire announced itself
 *   as "Modalidad: Overnight" -- an internal enum value, in English, naming
 *   a night nobody is spending. The two rows that replaced it say what the
 *   parties actually need to remember about a machine hire: whether it is
 *   being delivered, and whether an operator comes with it;
 * - it built a price line out of the listing's CURRENT daily rate times a
 *   day count and printed the booking's total beside it, so an edited
 *   listing turned into "2 días × 700,00€ ...... 1810,00€". A past sale is
 *   not re-priceable from today's listing: the total is the total, and what
 *   it is made of lives in QuoteBreakdownSheet, which already itemises the
 *   real lines. This panel links to it rather than paraphrasing it;
 * - it decided the address from `status === 'Confirmed'` alone -- see
 *   bookingLocationHelper for what was wrong with that, and for the
 *   delivered booking's second address that nothing used to show.
 *
 * "Cancelar reserva" here is only ever a visual entry point -- Messages.tsx
 * owns the one cancelBooking call, shared with the thread's own quick
 * action so the behaviour never diverges between the two buttons.
 */
const BookingDetailsPanel: React.FC<BookingDetailsPanelProps> = ({ booking, onClose, canCancel, cancelling, onCancel }) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const { userId } = useAuth();
  const [space, setSpace] = useState<IHeavyDutyMachinery | undefined>(undefined);
  const [loadingSpace, setLoadingSpace] = useState(true);
  const [cancelSheetOpen, setCancelSheetOpen] = useState(false);
  const [invoices, setInvoices] = useState<IInvoice[]>([]);
  const [downloadingInvoiceId, setDownloadingInvoiceId] = useState<string | undefined>(undefined);
  const [taxInfoInvoice, setTaxInfoInvoice] = useState<IInvoice | undefined>(undefined);
  const [breakdownOpen, setBreakdownOpen] = useState(false);

  // Only ever used to pick WORDING (see bookingLocationHelper) -- never to
  // decide what may be shown. The server already withheld anything this
  // viewer isn't entitled to.
  const isHost = !!userId && booking.guestUserId !== userId;
  const location = buildBookingLocationView(booking, isHost);

  useEffect(() => {
    setLoadingSpace(true);
    getHeavyDutyMachineryById(booking.heavyDutyMachineryId)
      .then(setSpace)
      .finally(() => setLoadingSpace(false));
  }, [booking.heavyDutyMachineryId]);

  useEffect(() => {
    // Empty (not an error) for bookings with no invoice yet -- still Pending, or an audit stay.
    getInvoicesForBooking(booking.id)
      .then(setInvoices)
      .catch(() => setInvoices([]));
  }, [booking.id]);

  const invoiceTypeLabel = (type: string): string =>
    type === 'PlatformCommission'
      ? translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_INVOICE_TYPE_PLATFORM_COMMISSION)
      : translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_INVOICE_TYPE_HOST_TO_GUEST);

  const invoiceKindLabel = (kind: string): string => {
    if (kind === 'Complete') return translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_INVOICE_KIND_COMPLETE);
    if (kind === 'SimplifiedQualified') return translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_INVOICE_KIND_SIMPLIFIED_QUALIFIED);
    return translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_INVOICE_KIND_SIMPLIFIED);
  };

  const handleDownloadInvoice = async (invoice: IInvoice) => {
    setDownloadingInvoiceId(invoice.id);
    try {
      await downloadInvoice(invoice);
    } catch {
      message.error(translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_INVOICE_DOWNLOAD_ERROR));
    } finally {
      setDownloadingInvoiceId(undefined);
    }
  };

  const total = `${booking.totalPrice.toFixed(2)}€`;

  return (
    <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: `1px solid ${token.colorBorderSecondary}` }}>
        <Title level={5} style={{ margin: 0 }}>
          {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_MESSAGES_DETAILS_PANEL_TITLE)}
        </Title>
        <Button type="text" size="small" aria-label="close-details-panel" icon={<IconX size={16} />} onClick={onClose} />
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
        {loadingSpace ? (
          <div style={{ textAlign: 'center', padding: 24 }}>
            <Spin size="small" />
          </div>
        ) : space?.latitude != null && space?.longitude != null ? (
          <StaticMap latitude={space.latitude} longitude={space.longitude} width={228} height={140} />
        ) : (
          <div
            style={{
              width: '100%',
              height: 140,
              borderRadius: 8,
              background: token.colorFillQuaternary,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 4,
              color: token.colorTextTertiary,
            }}
          >
            <IconMapOff size={20} />
            <Text type="secondary" style={{ fontSize: 12 }}>
              {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_MESSAGES_MAP_PLACEHOLDER)}
            </Text>
          </div>
        )}

        <Title level={5} style={{ marginTop: 12, marginBottom: 0 }}>
          {booking.heavyDutyMachineryTitle}
        </Title>
        {/* Copyable here and on the tracking page, not on the lists: this is
            where somebody is about to quote it into an email or read it down
            a phone, and a copy icon on every row of a list is just noise. */}
        <BookingReferenceTag reference={booking.reference} copyable style={{ display: 'block', marginBottom: 2 }} />
        {/* Labelled, because on a delivered booking this is no longer "the
            address" but one of two: where the machine starts from, and
            where it is going. Unlabelled, the pair reads as a contradiction. */}
        <div style={{ marginTop: 4 }}>
          <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4 }}>
            {translate(t, location.pickupLabelKey)}
          </Text>
          {location.pickupAddress ? (
            <Text style={{ fontSize: 13, display: 'flex', alignItems: 'flex-start', gap: 4 }}>
              <IconMapPin size={14} style={{ marginTop: 3, flexShrink: 0 }} />
              {location.pickupAddress}
            </Text>
          ) : (
            <Text type="secondary" style={{ fontSize: 12, fontStyle: 'italic', display: 'block' }}>
              {translate(t, location.pickupMissingKey!)}
            </Text>
          )}
        </div>

        {location.delivered && (
          <div style={{ marginTop: 10 }}>
            <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4 }}>
              {translate(t, location.deliveryLabelKey)}
            </Text>
            {/* A map rather than a pair of decimal coordinates: the guest
                marked this spot ON a map, and "37.38, -5.99" is not
                something a driver can act on. No apology when it is
                missing -- checkout will not let a delivery through without
                a pin, so the only bookings that get here are old ones, and
                the site notes below are then all there is to say. */}
            {location.deliveryPoint && (
              <>
                <StaticMap latitude={location.deliveryPoint.latitude} longitude={location.deliveryPoint.longitude} width={228} height={110} />
                <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 2 }}>
                  {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_LOCATION_DELIVERY_POINT_HINT)}
                </Text>
              </>
            )}
            {location.deliveryInstructions && (
              <Text style={{ fontSize: 12, display: 'block', marginTop: 4 }}>{location.deliveryInstructions}</Text>
            )}
          </div>
        )}

        {booking.cancellationReason && (
          <div style={{ marginTop: 8, padding: '8px 10px', borderRadius: 10, background: token.colorErrorBg }}>
            <Text type="danger" style={{ fontSize: 12 }}>
              {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_HOST_CANCEL_REASON_LABEL)}: {booking.cancellationReason}
            </Text>
          </div>
        )}

        <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {/* A daily rental's endAt is exclusive, so printing it raw would
              name a day nobody rents -- show the last day actually rented,
              and without a time of day, which is meaningless here. */}
          {isDailyRentalMode(booking.rentalMode) ? (
            <>
              <Row label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_FIRST_DAY_LABEL)} value={dayjs(booking.startAt).format('DD MMM')} />
              <Row label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_LAST_DAY_LABEL)} value={lastRentalDay(booking.endAt).format('DD MMM')} />
            </>
          ) : (
            <>
              <Row label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_CHECKIN_LABEL)} value={dayjs(booking.startAt).format('DD MMM, HH:mm')} />
              <Row label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_CHECKOUT_LABEL)} value={dayjs(booking.endAt).format('DD MMM, HH:mm')} />
            </>
          )}
          <Row label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_HANDOVER_LABEL)} value={translate(t, handoverModeKey(booking))} />
          <Row
            label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_OPERATOR_ROW_LABEL)}
            value={booking.operatorName ?? translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_OPERATOR_ROW_NONE)}
          />
        </div>

        <div style={{ marginTop: 16, paddingTop: 12, borderTop: `1px solid ${token.colorBorderSecondary}`, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Text strong>{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_MESSAGES_TOTAL_LABEL)}</Text>
            <Text strong>{total}</Text>
          </div>
          {/* Shown UNDER the total rather than replacing it. totalPrice is
              still what was agreed and invoiced -- the rectificativa is
              written against it -- so overwriting it here would make this
              panel disagree with the paperwork sitting right below. What
              both parties actually need is the pair: the price, and what
              came back off it. */}
          {(booking.refundedAmount ?? 0) > 0 && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text style={{ color: CORAL }}>
                  {t(TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_REFUNDED_BADGE, {
                    amount: `${(booking.refundedAmount ?? 0).toFixed(2)}€`,
                  })}
                </Text>
                <Text style={{ color: CORAL }}>{`-${(booking.refundedAmount ?? 0).toFixed(2)}€`}</Text>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text strong>
                  {t(TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_NET_AFTER_REFUND, {
                    amount: `${(booking.totalPrice - (booking.refundedAmount ?? 0)).toFixed(2)}€`,
                  })}
                </Text>
              </div>
            </>
          )}

          {/* A link to the itemised sheet, not a third rendering of it.
              The breakdown already exists in one place that is right by
              construction (it is the quote's own lines); paraphrasing it
              here is how this panel came to disagree with it. */}
          <Button
            type="text"
            size="small"
            icon={<IconFileInvoice size={14} />}
            style={{ alignSelf: 'flex-start', padding: 0, height: 'auto', fontSize: 12 }}
            onClick={() => setBreakdownOpen(true)}
          >
            {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_MESSAGES_VIEW_BREAKDOWN_BUTTON)}
          </Button>
        </div>

        {invoices.length > 0 && (
          <div style={{ marginTop: 16, paddingTop: 12, borderTop: `1px solid ${token.colorBorderSecondary}` }}>
            <Text strong style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
              <IconReceipt2 size={16} />
              {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_INVOICES_SECTION_TITLE)}
            </Text>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {invoices.map((invoice) => (
                <div key={invoice.id}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <Text style={{ fontSize: 13, display: 'block' }}>{invoiceTypeLabel(invoice.type)}</Text>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {invoice.invoiceNumber} · {invoice.amount.toFixed(2)}€ · {invoiceKindLabel(invoice.kind)}
                      </Text>
                    </div>
                    <Button
                      type="text"
                      size="small"
                      icon={<IconDownload size={14} />}
                      loading={downloadingInvoiceId === invoice.id}
                      onClick={() => handleDownloadInvoice(invoice)}
                    >
                      {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_INVOICE_DOWNLOAD_BUTTON)}
                    </Button>
                  </div>
                  {invoice.type === 'HostToGuest' && !invoice.recipientTaxId && (
                    <Button type="link" size="small" style={{ padding: 0, height: 'auto', fontSize: 12 }} onClick={() => setTaxInfoInvoice(invoice)}>
                      {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_INVOICE_ADD_TAX_ID_BUTTON)}
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {canCancel && (
        <div style={{ padding: 16, borderTop: `1px solid ${token.colorBorderSecondary}` }}>
          <Button
            block
            icon={<IconX size={14} />}
            loading={cancelling}
            style={{ borderRadius: 999, border: `1px solid ${token.colorBorder}`, background: 'transparent' }}
            onClick={() => setCancelSheetOpen(true)}
          >
            {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_CANCEL_BUTTON)}
          </Button>
        </div>
      )}

      <ConfirmBottomSheet
        open={cancelSheetOpen}
        title={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_CANCEL_CONFIRM)}
        description={`${booking.heavyDutyMachineryTitle} · ${dayjs(booking.startAt).format('D MMM, HH:mm')} – ${dayjs(booking.endAt).format('D MMM, HH:mm')}`}
        cancelText={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_CANCEL_SHEET_KEEP)}
        confirmText={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_CANCEL_SHEET_CONFIRM)}
        confirmLoading={cancelling}
        onConfirm={() => {
          setCancelSheetOpen(false);
          onCancel();
        }}
        onClose={() => setCancelSheetOpen(false)}
      />

      {/* Read-only on purpose: every decision button this sheet can show is
          already offered by the thread's own pinned card, and a second place
          to accept a quote is a second place for the two to disagree. */}
      <QuoteBreakdownSheet open={breakdownOpen} booking={booking} userId={userId ?? undefined} onClose={() => setBreakdownOpen(false)} />

      {taxInfoInvoice && (
        <AddInvoiceTaxInfoModal
          open
          invoice={taxInfoInvoice}
          onClose={() => setTaxInfoInvoice(undefined)}
          onUpdated={(updated) => setInvoices((prev) => prev.map((i) => (i.id === updated.id ? updated : i)))}
        />
      )}
    </div>
  );
};

const Row: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
    <Text type="secondary">{label}</Text>
    <Text>{value}</Text>
  </div>
);

export default BookingDetailsPanel;
